import {
  TextField,
  Grid2 as Grid,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { MainLayoutContainer } from "components/Layout";
import {
  useAddAddressMutation,
  useAddSiteMutation,
  useGetSiteByIdLazyQuery,
  useUpdateAddressMutation,
  useUpdateSiteMutation,
} from "graphql/__generated__/hooks";
import { useEffect } from "react";
import { Address } from "types/site";
import { useNavigate, useParams } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import { useAppDispatch } from "hooks/useAppDispatch";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { useNotifications } from "hooks/useNotifications";
import { Messages, NotificationSeverity } from "helpers/constants";

type SiteInput = Address & { name: string };

const initialValues: SiteInput = {
  name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  country: "",
  zip_code: "",
};

const getValidationSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("This field is required"),
    address_line_1: Yup.string().required("This field is required"),
    address_line_2: Yup.string(),
    city: Yup.string().required("This field is required"),
    state: Yup.string().required("This field is required"),
    country: Yup.string().required("This field is required"),
    zip_code: Yup.string(),
  });

const StyledFormicError = styled("div")(() => ({
  color: "red",
  padding: 0,
  margin: 0,
}));

export const SiteCreateOrEdit = () => {
  const { siteId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const backRoute = AdminRoutes.SitesList;

  const [getSiteById, { data: dataSite }] = useGetSiteByIdLazyQuery();
  const [addSite, { loading }] = useAddSiteMutation();
  const [updateSite, { loading: updateLoading }] = useUpdateSiteMutation();
  const [updateAddress] = useUpdateAddressMutation();
  const [addAddress] = useAddAddressMutation();

  const { notify } = useNotifications();

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      if (siteId) {
        await editSite(values);
      } else {
        await createSite(values);
      }
    },
  });

  useEffect(() => {
    if (siteId) {
      getSiteById({ variables: { siteId } }).then();
    }
  }, [siteId]);

  useEffect(() => {
    if (dataSite) {
      formik.setValues({
        name: dataSite.site_by_pk?.name ?? "",
        address_line_1: dataSite.site_by_pk?.address?.address_line_1 ?? "",
        address_line_2: dataSite.site_by_pk?.address?.address_line_2 ?? "",
        city: dataSite.site_by_pk?.address?.city ?? "",
        state: dataSite.site_by_pk?.address?.state ?? "",
        country: dataSite.site_by_pk?.address?.country ?? "",
        zip_code: dataSite.site_by_pk?.address?.zip_code ?? "",
      });
    }
  }, [dataSite]);

  const editSite = async (values: SiteInput) => {
    await updateAddress({
      variables: {
        id: dataSite?.site_by_pk?.address?.address_id,
        address: {
          address_line_1: values.address_line_1,
          address_line_2: values.address_line_2,
          city: values.city,
          state: values.state,
          country: values.country,
          zip_code: values.zip_code,
        },
      },
    }).then();

    updateSite({
      variables: {
        id: siteId,
        site: {
          name: values.name,
        },
      },
    }).then(() => {
      navigate(backRoute);
      notify({
        message: Messages.successSiteUpdated,
        severity: NotificationSeverity.success,
      });
    });
  };

  const createSite = async (values: SiteInput) => {
    const { data: addressData } = await addAddress({
      variables: {
        address_line_1: values.address_line_1,
        address_line_2: values.address_line_2 ?? "",
        city: values.city,
        state: values.state,
        country: values.country,
        zip_code: values.zip_code ?? "",
      },
    });
    const addressId = addressData?.insert_address?.returning[0].address_id;

    console.log(addressId);

    if (addressId === undefined) {
      dispatch(
        showNotificationThunk({
          message: "Failed to create address",
          severity: "error",
        }),
      );
      return;
    }

    addSite({
      variables: {
        name: values.name,
        address: addressId,
      },
    })
      .then(() => {
        navigate(backRoute);
        notify({
          message: Messages.successSiteCreated,
          severity: NotificationSeverity.success,
        });
      })
      .catch(() => {
        notify({
          message: Messages.errorSiteCreated,
          severity: NotificationSeverity.error,
        });
      });
  };

  return (
    <MainLayoutContainer
      title={`${siteId ? "Edit" : "Create"} Site`}
      breadcrumb={BREADCRUMB_PAGE.SITES}
    >
      <Box sx={{ maxWidth: 860 }} mt={2}>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Box>
              <TextField
                variant="outlined"
                onChange={formik.handleChange}
                margin="dense"
                required
                fullWidth
                name="name"
                label="Name"
                type="text"
                id="name"
                value={formik?.values?.name}
              />
              <ErrorMessage name="name" component={StyledFormicError} />
            </Box>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Address
            </Typography>

            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  onChange={formik.handleChange}
                  margin="dense"
                  required
                  fullWidth
                  name="address_line_1"
                  label="Address Line 1"
                  type="text"
                  id="address_line_1"
                  value={formik?.values?.address_line_1}
                />
                <ErrorMessage
                  name="address_line_1"
                  component={StyledFormicError}
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  variant="outlined"
                  onChange={formik.handleChange}
                  margin="dense"
                  required
                  fullWidth
                  name="address_line_2"
                  label="Address Line 2"
                  type="text"
                  id="address_line_2"
                  value={formik?.values?.address_line_2}
                />
                <ErrorMessage
                  name="address_line_2"
                  component={StyledFormicError}
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  variant="outlined"
                  onChange={formik.handleChange}
                  margin="dense"
                  required
                  fullWidth
                  name="city"
                  label="City"
                  type="text"
                  id="city"
                  value={formik?.values?.city}
                />
                <ErrorMessage name="city" component={StyledFormicError} />
              </Grid>
              <Grid size={6}>
                <TextField
                  variant="outlined"
                  onChange={formik.handleChange}
                  margin="dense"
                  required
                  fullWidth
                  name="state"
                  label="State"
                  type="text"
                  id="state"
                  value={formik?.values?.state}
                />
                <ErrorMessage name="state" component={StyledFormicError} />
              </Grid>
              <Grid size={6}>
                <TextField
                  variant="outlined"
                  onChange={formik.handleChange}
                  margin="dense"
                  required
                  fullWidth
                  name="country"
                  label="Country"
                  type="text"
                  id="country"
                  value={formik?.values?.country}
                />
                <ErrorMessage name="country" component={StyledFormicError} />
              </Grid>
              <Grid size={6}>
                <TextField
                  variant="outlined"
                  onChange={formik.handleChange}
                  margin="dense"
                  required
                  fullWidth
                  name="zip_code"
                  label="Zip Code"
                  type="text"
                  id="zip_code"
                  value={formik?.values?.zip_code}
                />
                <ErrorMessage name="zip_code" component={StyledFormicError} />
              </Grid>
            </Grid>
            <Box mt={4}>
              <Divider />
            </Box>
            <Box mt={2}>
              <Stack direction="row-reverse" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading || updateLoading}
                >
                  Save Changes
                </Button>
                <Button variant="outlined" onClick={() => navigate(backRoute)}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          </form>
        </FormikProvider>
      </Box>
    </MainLayoutContainer>
  );
};
